export type DistributionRangeId = "7d" | "30d" | "custom";

export type Selection = { id: Exclude<DistributionRangeId, "custom"> } | { id: "custom"; from: string; to: string };

export type DistributionRange = {
  id: DistributionRangeId;
  from: string;
  to: string;
};

export const PRESET_RANGES: Array<{ id: Exclude<DistributionRangeId, "custom">; label: string; days: number }> = [
  { id: "7d", label: "Last 7 days", days: 7 },
  { id: "30d", label: "Last 30 days", days: 30 },
];

export const DEFAULT_RANGE_ID = "30d" as const;

const iso = (date: Date) => date.toISOString().slice(0, 10);

// Anchored on the viewer's calendar day, not the UTC one: west of Greenwich the two
// disagree for the last hours of the evening, and taking UTC there ends the window on
// a date that has not happened locally yet.
const startOfLocalDay = (date: Date) => new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

export const presetRange = (
  id: Exclude<DistributionRangeId, "custom">,
  today: Date,
  latestDataDate?: string,
): DistributionRange => {
  const preset = PRESET_RANGES.find((range) => range.id === id) ?? PRESET_RANGES[1];

  // The pipeline lands a day at a time, so anchoring on today would always leave the
  // last bar empty. Ending on the merchant's most recent day keeps the window full.
  const localToday = iso(startOfLocalDay(today));
  const anchor = latestDataDate && latestDataDate < localToday ? latestDataDate : localToday;

  const to = new Date(`${anchor}T00:00:00Z`);
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - (preset.days - 1));

  return { id, from: iso(from), to: iso(to) };
};

// The current day is never selectable: the pipeline lands a day at a time, so it holds
// no rows yet. When the sync is further behind than that, the last day it did land caps
// the picker instead.
export const latestSelectableDate = (today: Date, latestDataDate?: string): string => {
  const yesterday = startOfLocalDay(today);
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);

  const cap = iso(yesterday);
  return latestDataDate && latestDataDate < cap ? latestDataDate : cap;
};

const CUSTOM_LABEL_FORMAT: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" };

export const customRangeLabel = (from: string, to: string, locale?: string): string => {
  const formatter = new Intl.DateTimeFormat(locale, { ...CUSTOM_LABEL_FORMAT, timeZone: "UTC" });
  return `${formatter.format(new Date(`${from}T00:00:00Z`))} – ${formatter.format(new Date(`${to}T00:00:00Z`))}`;
};

const rangeLabel = (range: DistributionRange, locale?: string): string =>
  range.id === "custom"
    ? customRangeLabel(range.from, range.to, locale)
    : (PRESET_RANGES.find((preset) => preset.id === range.id)?.label ?? "");

export const headingFor = (range: DistributionRange, locale?: string): string =>
  range.id === "custom"
    ? `Your ${customRangeLabel(range.from, range.to, locale)} distribution`
    : `Your ${rangeLabel(range, locale).toLowerCase()} distribution`;

export const noteRangePhrase = (range: DistributionRange, locale?: string): string =>
  range.id === "custom" ? customRangeLabel(range.from, range.to, locale) : rangeLabel(range, locale).toLowerCase();

const DAY_MS = 86_400_000;

export const daysInRange = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS) + 1;

export const priorRangePhrase = (range: DistributionRange, locale?: string): string => {
  const preset = PRESET_RANGES.find((entry) => entry.id === range.id);
  if (preset) return `previous ${preset.days} days`;

  const to = Date.parse(`${range.from}T00:00:00Z`) - DAY_MS;
  const days = daysInRange(range.from, range.to);
  return customRangeLabel(iso(new Date(to - (days - 1) * DAY_MS)), iso(new Date(to)), locale);
};

const BUCKET_LABEL_OPTIONS: Record<"daily" | "weekly" | "monthly", Intl.DateTimeFormatOptions> = {
  daily: { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" },
  weekly: { day: "numeric", month: "short", timeZone: "UTC" },
  monthly: { month: "long", year: "numeric", timeZone: "UTC" },
};

export const bucketLabel = (
  bucket: "daily" | "weekly" | "monthly",
  periodStart: string,
  periodEnd: string,
  locale?: string,
): string => {
  const formatter = new Intl.DateTimeFormat(locale, BUCKET_LABEL_OPTIONS[bucket]);
  const start = formatter.format(new Date(`${periodStart}T00:00:00Z`));

  if (bucket !== "weekly") return start;
  return `${start} – ${formatter.format(new Date(`${periodEnd}T00:00:00Z`))}`;
};

export const waitingLabel = (oldestPendingReviewAt: string, now: Date): string => {
  const hours = Math.max(0, Math.floor((now.getTime() - Date.parse(oldestPendingReviewAt)) / 3_600_000));
  if (hours < 48) return `${hours} ${hours === 1 ? "hour" : "hours"}`;

  const days = Math.floor(hours / 24);
  return `${days} days`;
};
